/**
 * Funnel Analysis Page
 * Page drop-off & funnel visualization with Sankey diagrams
 */

import { useState, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Filter,
  AlertTriangle,
  TrendingDown,
  Users,
  Clock,
  RefreshCw,
  Download,
  ChevronRight,
  Info,
  BarChart3,
  Table as TableIcon,
  ArrowUpDown,
  ChevronLeft,
  ChevronRight as ChevronRightIcon,
  Monitor,
  Smartphone,
  FileText,
  LogOut,
  TrendingUp,
  Activity,
  DollarSign,
  Flame,
  XCircle,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from 'lucide-react';
import { api } from '../../services/api';
import SankeyDiagram from '../../components/analytics/SankeyDiagram';
import SessionDrillDownModal from '../../components/analytics/SessionDrillDownModal';
import { LoopAnalysis } from '../../components/analytics/LoopAnalysis';
import { useDataSourceStore } from '@store/dataSourceStore';
import { useAuthStore } from '@store/authStore';
import { useWebSocket } from '@hooks/useWebSocket';
import DateRangeFilter, { DateRange } from '@components/analytics/DateRangeFilter';
import toast from 'react-hot-toast';

interface SankeyNode {
  name: string;
  id: string;
  sessions?: string[]; // All session IDs (including stitched) for drill-down
  journeyCount?: number; // Number of unique STITCHED journeys (for display)
  utmSources?: { source: string; count: number }[];
  utmCampaigns?: { campaign: string; count: number }[];
}

interface SankeyLink {
  source: string;
  target: string;
  value: number;
  sessions: string[];
}

interface Session {
  sessionId: string;
  totalPages: number;
  firstPage: string;
  lastPage: string;
  device: string;
  userId?: string;
  utmSource?: string;
}

interface Anomaly {
  page: string;
  metric: string;
  current: number;
  baseline: number;
  deviation: number;
  severity: 'low' | 'medium' | 'high' | 'critical';
  timestamp: string;
  affected_sessions: number;
}

const FunnelAnalysisPage = () => {
  // Get selected company and data source from store
  const { selectedCompany, selectedDataSources, getSelection } = useDataSourceStore();

  // Initialize queryClient for cache updates
  const queryClient = useQueryClient();

  // **NEW: State for AI insights loading**
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  
  // **NEW: State for expandable accordions in modal**
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  
  // **NEW: State for loading abandoned sessions**
  const [loadingAbandonedSessions, setLoadingAbandonedSessions] = useState(false);
  const [abandonedSessions, setAbandonedSessions] = useState<any[]>([]);

  // **NEW: Get AI insights for a specific stat from OpenAI**
  const fetchAIInsights = async (statType: string, data: any) => {
    setLoadingInsights(true);
    setAiInsights(null);
    
    try {
      // **SAFETY: Ensure sankeyData is loaded before making the request**
      if (!sankeyData?.stats) {
        console.warn('⚠️ Sankey data not loaded yet, waiting...');
        setAiInsights('Loading analytics data...');
        setTimeout(() => {
          if (sankeyData?.stats) {
            fetchAIInsights(statType, data);
          } else {
            setAiInsights('Unable to load analytics data. Please refresh the page and try again.');
            setLoadingInsights(false);
          }
        }, 1000);
        return;
      }

      const url = `/api/v1/ai/insights`;
      const token = useAuthStore.getState().accessToken;
      
      console.log('🤖 Fetching AI insights...', {
        url,
        statType,
        hasToken: !!token,
        tokenPreview: token ? `${token.substring(0, 20)}...` : 'null',
        data: data,
        hasSankeyData: !!sankeyData?.stats,
      });
      
      const requestBody = {
        type: 'funnel_conversion_stat',
        statType,
        statData: data,
        context: {
          totalSessions: sankeyData.stats.total_sessions || 0,
          dateRange: {
            startDate: typeof dateRange.startDate === 'string' 
              ? dateRange.startDate 
              : dateRange.startDate?.toISOString?.() || new Date().toISOString(),
            endDate: typeof dateRange.endDate === 'string' 
              ? dateRange.endDate 
              : dateRange.endDate?.toISOString?.() || new Date().toISOString(),
          },
          conversionFunnel: sankeyData.stats.conversion_funnel || {},
          dropOffAnalysis: sankeyData.stats.drop_off_analysis || {},
        },
      };
      
      console.log('📦 Request body:', JSON.stringify(requestBody, null, 2));
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestBody),
      });

      console.log('📡 AI insights response:', {
        status: response.status,
        statusText: response.statusText,
        ok: response.ok,
        headers: Object.fromEntries(response.headers.entries()),
      });

      if (!response.ok) {
        const responseText = await response.text();
        console.error('❌ AI insights error response (raw):', responseText);
        
        let errorData: any = {};
        try {
          errorData = JSON.parse(responseText);
        } catch (e) {
          console.error('Failed to parse error response as JSON');
        }
        
        console.error('❌ AI insights error response (parsed):', errorData);
        throw new Error(errorData.message || errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('✅ AI insights received:', result);
      
      setAiInsights(result.data?.insights || result.insights || 'No insights available at this time.');
    } catch (error: any) {
      console.error('❌ Error fetching AI insights:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Unable to generate insights. ';
      
      if (error.message?.includes('Failed to fetch') || error.message?.includes('NetworkError')) {
        errorMessage += `Cannot connect to AI service. Please ensure the AI service is running.`;
      } else if (error.message?.includes('401') || error.message?.includes('403')) {
        errorMessage += 'Authentication failed. Please log in again.';
      } else if (error.message?.includes('404')) {
        errorMessage += 'AI insights endpoint not found. Please check the service configuration.';
      } else if (error.message?.includes('500')) {
        errorMessage += 'AI service error. This might be due to missing OpenAI API key or service misconfiguration.';
      } else {
        errorMessage += error.message || 'Please try again later.';
      }
      
      setAiInsights(errorMessage);
    } finally {
      setLoadingInsights(false);
    }
  };

  // Filter to only use data sources that support 'funnel' module
  const funnelDataSources = selectedDataSources.filter(ds =>
    (ds.availableModules || []).includes('funnel')
  );
  const selectedDataSource = funnelDataSources[0] || null;

  const { companyId } = getSelection();
  const dataSourceId = selectedDataSource?.id || null;

  // WebSocket for real-time updates
  const { socket, connected } = useWebSocket({ autoConnect: true });

  // **LOAD SAVED FILTERS FROM LOCALSTORAGE BEFORE INITIALIZING STATE**
  const loadSavedFilters = () => {
    try {
      const savedFilters = localStorage.getItem('funnelAnalysisFilters');
      if (savedFilters) {
        return JSON.parse(savedFilters);
      }
    } catch (error) {
      console.error('Failed to parse saved funnel filters:', error);
    }
    return null;
  };

  const savedFilters = loadSavedFilters();

  // Date range state (default: Last 7 days, or load from localStorage)
  const getDefaultDateRange = (): DateRange => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 7);
    return {
      startDate: startDate.toISOString(),
      endDate: endDate.toISOString(),
      label: 'Last 7 days',
    };
  };

  const [dateRange, setDateRange] = useState<DateRange>(
    savedFilters?.dateRange || getDefaultDateRange()
  );
  
  // View mode state (graph/sankey or table/list)
  const [viewMode, setViewMode] = useState<'graph' | 'table'>('graph');
  
  // Table sorting and pagination
  const [sortBy, setSortBy] = useState<'source' | 'target' | 'sessions' | 'dropOffRate'>('sessions');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  
  // Advanced filter state with operators (load from localStorage)
  const [firstPageFilter, setFirstPageFilter] = useState<{
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'notContains';
    value: string;
  }>(savedFilters?.firstPageFilter || { operator: 'contains', value: '' });
  
  const [lastPageFilter, setLastPageFilter] = useState<{
    operator: 'contains' | 'equals' | 'startsWith' | 'endsWith' | 'notContains';
    value: string;
  }>(savedFilters?.lastPageFilter || { operator: 'contains', value: '' });
  
  const [showFilters, setShowFilters] = useState(false);

  // Autocomplete states
  const [firstPageSearch, setFirstPageSearch] = useState('');
  const [lastPageSearch, setLastPageSearch] = useState('');
  const [showFirstPageDropdown, setShowFirstPageDropdown] = useState(false);
  const [showLastPageDropdown, setShowLastPageDropdown] = useState(false);

  // **NEW FILTERS** for better visualization (load from localStorage)
  const [utmSourceFilter, setUtmSourceFilter] = useState<string>(savedFilters?.utmSourceFilter || 'all');
  const [utmMediumFilter, setUtmMediumFilter] = useState<string>(savedFilters?.utmMediumFilter || 'all');
  const [utmCampaignFilter, setUtmCampaignFilter] = useState<string>(savedFilters?.utmCampaignFilter || 'all');
  const [utmTermFilter, setUtmTermFilter] = useState<string>(savedFilters?.utmTermFilter || 'all');
  const [utmContentFilter, setUtmContentFilter] = useState<string>(savedFilters?.utmContentFilter || 'all');
  const [deviceTypeFilter, setDeviceTypeFilter] = useState<string>(savedFilters?.deviceTypeFilter || 'all');

  // Selected link sessions
  const [selectedLink, setSelectedLink] = useState<{ source: string; target: string; sessions?: string[] } | null>(null);
  const [selectedSession, setSelectedSession] = useState<string | null>(null);
  const [selectedSessionStitchedIds, setSelectedSessionStitchedIds] = useState<string[]>([]);
  const [isDrillDownOpen, setIsDrillDownOpen] = useState(false);

  // Loop analysis (back navigation patterns removed from Sankey)
  const [loopPatterns, setLoopPatterns] = useState<any[]>([]);

  // **NEW: Interactive stats modal state**
  const [selectedStat, setSelectedStat] = useState<{
    title: string;
    type: 'conversion' | 'drop-off' | 'engagement';
    data: any;
  } | null>(null);

  // Save filters to localStorage whenever they change
  useEffect(() => {
    const filtersToSave = {
      dateRange,
      firstPageFilter,
      lastPageFilter,
      utmSourceFilter,
      utmMediumFilter,
      utmCampaignFilter,
      utmTermFilter,
      utmContentFilter,
      deviceTypeFilter,
    };
    localStorage.setItem('funnelAnalysisFilters', JSON.stringify(filtersToSave));
  }, [
    dateRange,
    firstPageFilter,
    lastPageFilter,
    utmSourceFilter,
    utmMediumFilter,
    utmCampaignFilter,
    utmTermFilter,
    utmContentFilter,
    deviceTypeFilter,
  ]);

  // Clear all filters function
  const handleClearAllFilters = () => {
    setDateRange(getDefaultDateRange());
    setFirstPageFilter({ operator: 'contains', value: '' });
    setLastPageFilter({ operator: 'contains', value: '' });
    setUtmSourceFilter('all');
    setUtmMediumFilter('all');
    setUtmCampaignFilter('all');
    setUtmTermFilter('all');
    setUtmContentFilter('all');
    setDeviceTypeFilter('all');
    setFirstPageSearch('');
    setLastPageSearch('');
    console.log('🧹 Cleared all funnel filters');
  };

  // **FETCH REAL FILTER OPTIONS FROM DATABASE**
  // **OPTIMIZED: Aggressive caching for filter dropdowns with cascading logic**

  // Fetch available UTM sources
  const { data: utmSources, isLoading: isLoadingUtmSources } = useQuery({
    queryKey: ['utm-sources', companyId, dataSourceId, dateRange],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/funnel/utm-sources', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 15 * 60 * 1000, // 15 minutes (INCREASED)
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (INCREASED)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1, // Only retry once on failure
  });

  // Fetch available UTM campaigns
  const { data: utmCampaigns, isLoading: isLoadingUtmCampaigns } = useQuery({
    queryKey: ['utm-campaigns', companyId, dataSourceId, dateRange, utmSourceFilter],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/funnel/utm-campaigns', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
          utmSource: utmSourceFilter !== 'all' ? utmSourceFilter : undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 15 * 60 * 1000, // 15 minutes (INCREASED)
    gcTime: 30 * 60 * 1000, // Keep in cache for 30 minutes (INCREASED)
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1, // Only retry once on failure
  });

  // Fetch available UTM mediums (cascading: depends on source)
  const { data: utmMediums, isLoading: isLoadingUtmMediums } = useQuery({
    queryKey: ['utm-mediums', companyId, dataSourceId, dateRange, utmSourceFilter],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/funnel/utm-mediums', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
          utmSource: utmSourceFilter !== 'all' ? utmSourceFilter : undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Fetch available UTM terms (cascading: depends on source, medium, campaign)
  const { data: utmTerms, isLoading: isLoadingUtmTerms } = useQuery({
    queryKey: [
      'utm-terms',
      companyId,
      dataSourceId,
      dateRange,
      utmSourceFilter,
      utmMediumFilter,
      utmCampaignFilter,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/funnel/utm-terms', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
          utmSource: utmSourceFilter !== 'all' ? utmSourceFilter : undefined,
          utmMedium: utmMediumFilter !== 'all' ? utmMediumFilter : undefined,
          utmCampaign: utmCampaignFilter !== 'all' ? utmCampaignFilter : undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Fetch available UTM contents (cascading: depends on all previous UTM filters)
  const { data: utmContents, isLoading: isLoadingUtmContents } = useQuery({
    queryKey: [
      'utm-contents',
      companyId,
      dataSourceId,
      dateRange,
      utmSourceFilter,
      utmMediumFilter,
      utmCampaignFilter,
      utmTermFilter,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) return [];
      const response = await api.get('/api/v1/analytics/funnel/utm-contents', {
        params: {
          companyId,
          dataSourceId,
          startDate: dateRange.startDate,
          endDate: dateRange.endDate,
          website: 'quickmeds.co.uk',
          utmSource: utmSourceFilter !== 'all' ? utmSourceFilter : undefined,
          utmMedium: utmMediumFilter !== 'all' ? utmMediumFilter : undefined,
          utmCampaign: utmCampaignFilter !== 'all' ? utmCampaignFilter : undefined,
          utmTerm: utmTermFilter !== 'all' ? utmTermFilter : undefined,
        },
      });
      return response.data.data || [];
    },
    enabled: !!companyId && !!dataSourceId,
    staleTime: 15 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 1,
  });

  // Fetch Sankey data
  const {
    data: sankeyData,
    isLoading: isLoadingSankey,
    refetch: refetchSankey,
  } = useQuery({
    queryKey: [
      'sankey',
      dateRange.startDate,
      dateRange.endDate,
      firstPageFilter,
      lastPageFilter,
      utmSourceFilter,
      utmMediumFilter,
      utmCampaignFilter,
      utmTermFilter,
      utmContentFilter,
      deviceTypeFilter,
      companyId,
      dataSourceId,
      2, // minSessions
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      const params: any = {
        companyId,
        dataSourceId,
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        website: 'quickmeds.co.uk',
        minSessions: 2, // CRITICAL: Show paths with at least 2 sessions (backend default is 5!)
      };
      
      // Add first page filter with operator
      if (firstPageFilter && firstPageFilter.value.trim()) {
        params.firstPageOperator = firstPageFilter.operator;
        params.firstPage = firstPageFilter.value.trim();
      }
      
      // Add last page filter with operator
      if (lastPageFilter && lastPageFilter.value.trim()) {
        params.lastPageOperator = lastPageFilter.operator;
        params.lastPage = lastPageFilter.value.trim();
      }
      if (utmSourceFilter && utmSourceFilter !== 'all') params.utmSource = utmSourceFilter;
      if (utmMediumFilter && utmMediumFilter !== 'all') params.utmMedium = utmMediumFilter;
      if (utmCampaignFilter && utmCampaignFilter !== 'all') params.utmCampaign = utmCampaignFilter;
      if (utmTermFilter && utmTermFilter !== 'all') params.utmTerm = utmTermFilter;
      if (utmContentFilter && utmContentFilter !== 'all') params.utmContent = utmContentFilter;
      if (deviceTypeFilter && deviceTypeFilter !== 'all') params.deviceType = deviceTypeFilter;

      const response = await api.get('/api/v1/analytics/funnel/sankey', { params });
      const data = response.data.data;
      
      // **DEBUG: Log stats data**
      console.log('📊 Sankey data received:', {
        totalSessions: data.totalSessions,
        stats: data.stats,
        hasStats: !!data.stats,
        statsKeys: data.stats ? Object.keys(data.stats) : [],
        nodeCount: data.sankey?.nodes?.length || 0,
        linkCount: data.sankey?.links?.length || 0,
      });
      
      // **DEBUG: Log the entire response structure**
      console.log('📦 Full response.data:', response.data);
      
      return data;
    },
    enabled: !!companyId && !!dataSourceId, // Only fetch when company and data source are selected
    staleTime: 30000, // 30 seconds
  });

  // Extract available pages from Sankey data for autocomplete
  // Using useMemo to avoid re-renders and circular dependencies
  const availablePages = useMemo(() => {
    if (!sankeyData?.sankey?.nodes) return [];

    // Extract unique pages with session counts
    const pages = sankeyData.sankey.nodes.map((node: any) => ({
      page: node.id,
      name: node.name || node.id,
      count: node.sessions?.length || 0,
    }));

    // Sort by session count (most popular first)
    return pages.sort((a, b) => b.count - a.count);
  }, [sankeyData?.sankey?.nodes]);

  // **DEBUG: Log sankeyData whenever it changes**
  useEffect(() => {
    if (sankeyData) {
      console.log('🎯 SankeyData updated:', {
        totalSessions: sankeyData.totalSessions,
        hasStats: !!sankeyData.stats,
        stats: sankeyData.stats,
        statsKeys: sankeyData.stats ? Object.keys(sankeyData.stats) : [],
        firstStatValue: sankeyData.stats?.total_sessions,
      });
    }
  }, [sankeyData]);

  // WebSocket real-time updates - Listen to session events
  useEffect(() => {
    if (!socket || !connected || !companyId || !dataSourceId) return;

    const room = `sessions:${companyId}:${dataSourceId}`;
    
    console.log(`📡 Funnel subscribing to sessions room: ${room}`);
    
    // Subscribe to the same sessions room as SessionsPage
    socket.emit('sessions:subscribe', { companyId, dataSourceId });

    // Debounce Sankey refetch - only refetch after 5 seconds of no new events
    let refetchTimeout: NodeJS.Timeout | null = null;
    const debouncedRefetch = () => {
      if (refetchTimeout) clearTimeout(refetchTimeout);
      refetchTimeout = setTimeout(() => {
        console.log('🔄 Refetching Sankey data after session updates...');
        refetchSankey();
      }, 5000); // 5 seconds debounce
    };

    // Handle new sessions
    const handleNewSession = (data: any) => {
      console.log('🆕 Funnel: New session detected', data);
      
      // Update stats optimistically
      queryClient.setQueryData(
        [
          'sankey-data',
      companyId,
      dataSourceId,
          dateRange,
          firstPageFilter,
          lastPageFilter,
          utmSourceFilter,
          utmMediumFilter,
          utmCampaignFilter,
          utmTermFilter,
          utmContentFilter,
          deviceTypeFilter,
        ],
        (oldData: any) => {
          if (!oldData || !oldData.stats) return oldData;

          const newStats = { ...oldData.stats };
          newStats.total_sessions += 1;
          
          // Update device stats
          if (data.device_type) {
            if (/desktop|win32|mac|linux/i.test(data.device_type)) {
              newStats.desktop_sessions += 1;
            } else if (/mobile|iphone|android/i.test(data.device_type)) {
              newStats.mobile_sessions += 1;
            } else if (/tablet|ipad/i.test(data.device_type)) {
              newStats.tablet_sessions += 1;
            }
          }
          
          // Update traffic source stats
          if (!data.utm_source || data.utm_source === '' || data.utm_source === 'direct' || data.utm_source === '(direct)') {
            newStats.organic_sessions += 1;
          } else {
            newStats.paid_sessions += 1;
          }
          
          // Recalculate percentages
          const total = newStats.total_sessions;
          newStats.bounce_rate = ((newStats.bounce_sessions / total) * 100).toFixed(1);
          newStats.high_engagement_rate = ((newStats.high_engagement_sessions / total) * 100).toFixed(1);
          newStats.paid_traffic_rate = ((newStats.paid_sessions / total) * 100).toFixed(1);
          
          console.log('✅ Funnel stats updated optimistically', newStats);
          
          return { ...oldData, stats: newStats, totalSessions: total };
        }
      );

      // Debounce full Sankey refetch
      debouncedRefetch();
    };

    // Handle session updates (page count, duration changes)
    const handleSessionUpdate = (data: any) => {
      console.log('🔄 Funnel: Session updated', data);
      
      // For session updates, just debounce the refetch
      // Stats don't change much on updates, mainly on new sessions
      debouncedRefetch();
    };

    socket.on('sessions:new', handleNewSession);
    socket.on('sessions:updated', handleSessionUpdate);

    return () => {
      console.log('🔌 Funnel unsubscribing from sessions');
      if (refetchTimeout) clearTimeout(refetchTimeout);
      socket.off('sessions:new', handleNewSession);
      socket.off('sessions:updated', handleSessionUpdate);
      socket.emit('sessions:unsubscribe', { companyId, dataSourceId });
    };
  }, [socket, connected, companyId, dataSourceId, queryClient, dateRange, firstPageFilter, lastPageFilter, utmSourceFilter, utmMediumFilter, utmCampaignFilter, utmTermFilter, utmContentFilter, deviceTypeFilter]);

  // Fetch anomalies
  const { data: anomaliesData, isLoading: isLoadingAnomalies } = useQuery({
    queryKey: [
      'anomalies',
      dateRange,
      companyId,
      dataSourceId,
      firstPageFilter,
      lastPageFilter,
      utmSourceFilter,
      utmMediumFilter,
      utmCampaignFilter,
      utmTermFilter,
      utmContentFilter,
      deviceTypeFilter,
    ],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      const params: any = {
          companyId,
          dataSourceId,
          website: 'quickmeds.co.uk',
          lookbackDays: 7,
          comparisonDays: 30,
          threshold: 2,
      };

      // Add filters to anomalies query
      if (firstPageFilter && firstPageFilter.value.trim()) {
        params.firstPageOperator = firstPageFilter.operator;
        params.firstPage = firstPageFilter.value.trim();
      }
      if (lastPageFilter && lastPageFilter.value.trim()) {
        params.lastPageOperator = lastPageFilter.operator;
        params.lastPage = lastPageFilter.value.trim();
      }
      if (utmSourceFilter && utmSourceFilter !== 'all') params.utmSource = utmSourceFilter;
      if (utmMediumFilter && utmMediumFilter !== 'all') params.utmMedium = utmMediumFilter;
      if (utmCampaignFilter && utmCampaignFilter !== 'all') params.utmCampaign = utmCampaignFilter;
      if (utmTermFilter && utmTermFilter !== 'all') params.utmTerm = utmTermFilter;
      if (utmContentFilter && utmContentFilter !== 'all') params.utmContent = utmContentFilter;
      if (deviceTypeFilter && deviceTypeFilter !== 'all') params.deviceType = deviceTypeFilter;

      const response = await api.get('/api/v1/analytics/anomalies', { params });

      return response.data.data;
    },
    enabled: !!companyId && !!dataSourceId,
    refetchInterval: 60000, // Refresh every minute
  });

  // Fetch real-time alerts
  const { data: alertsData } = useQuery({
    queryKey: ['realtime-alerts', companyId, dataSourceId],
    queryFn: async () => {
      if (!companyId || !dataSourceId) {
        throw new Error('Please select a company and data source');
      }

      const response = await api.get('/api/v1/analytics/anomalies/realtime', {
        params: {
          website: 'quickmeds.co.uk',
          minutes: 60,
          dataSourceId,
        },
      });
      return response.data.data;
    },
    enabled: !!companyId && !!dataSourceId,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  // Fetch link sessions
  const { data: linkSessionsData, isLoading: isLoadingLinkSessions } = useQuery({
    queryKey: [
      'link-sessions',
      selectedLink,
      dateRange,
      companyId,
      dataSourceId,
      utmSourceFilter,
      utmCampaignFilter,
      deviceTypeFilter,
    ],
    queryFn: async () => {
      if (!selectedLink || !companyId || !dataSourceId) return null;

      console.log('🔍 Fetching link sessions with:', {
        source: selectedLink.source,
        target: selectedLink.target,
        sessionIds: selectedLink.sessions,
        companyId,
        dataSourceId,
      });

      // **USE POST to avoid URL length limits with many session IDs**
      const response = await api.post('/api/v1/analytics/funnel/link-sessions', {
        // Pass session IDs as array in body (avoids URL length limits)
        sessionIds: selectedLink.sessions || [],
        companyId,
        dataSourceId,
        // Keep source/target for display purposes
        source: selectedLink.source,
        target: selectedLink.target,
      });

      const data = response.data.data;
      
      // **DEBUG: Log session stitching results**
      console.log('📊 Link sessions response:', {
        inputSessionIds: selectedLink.sessions?.length,
        returnedTotal: data.total,
        returnedSessionsLength: data.sessions?.length,
        stitchingWorked: data.total < (selectedLink.sessions?.length || 0),
        firstSession: data.sessions?.[0],
      });

      return data;
    },
    enabled: !!selectedLink && !!companyId && !!dataSourceId,
  });

  // Fetch individual session
  const { data: sessionData, isLoading: isLoadingSession } = useQuery({
    queryKey: ['session', selectedSession, selectedSessionStitchedIds, companyId, dataSourceId],
    queryFn: async () => {
      if (!selectedSession || !companyId || !dataSourceId) return null;

      const response = await api.get(`/api/v1/analytics/funnel/session/${selectedSession}`, {
        params: {
          companyId,
          dataSourceId,
          stitchedSessionIds: selectedSessionStitchedIds.join(','), // Pass as comma-separated string
        },
      });

      return response.data.data;
    },
    enabled: !!selectedSession && !!companyId && !!dataSourceId,
  });

  // Handle link click - Open drill-down modal
  const handleLinkClick = (link: SankeyLink) => {
    setSelectedLink({ 
      source: link.source, 
      target: link.target,
      sessions: link.sessions // **NEW: Pass session IDs for accurate drill-down**
    });
    setSelectedSession(null);
    setIsDrillDownOpen(true);
  };

  // Handle node click - Open drill-down for this stage
    const handleNodeClick = (node: SankeyNode) => {
      console.log('🎯 Node clicked:', {
        id: node.id,
        name: node.name,
        journeyCount: node.journeyCount,
        sessionsLength: node.sessions?.length || 0,
        stitchingWorked: node.journeyCount && node.journeyCount < (node.sessions?.length || 0)
      });
    
    // Open drill-down showing all sessions that passed through this node
    setSelectedLink({ 
      source: node.id,
      target: node.id, // Same as source to indicate it's a node click
      sessions: node.sessions || []
    });
    setSelectedSession(null);
    setIsDrillDownOpen(true);
  };

  // Handle session click in drill-down
  const handleSessionClick = (sessionId: string, stitchedSessionIds?: string[]) => {
    setSelectedSession(sessionId);
    // Store stitched session IDs in state so we can pass them to the API
    setSelectedSessionStitchedIds(stitchedSessionIds || [sessionId]);
  };

  // Close drill-down modal
  const closeDrillDown = () => {
    setIsDrillDownOpen(false);
    setSelectedLink(null);
    setSelectedSession(null);
  };

  // Get severity badge
  const getSeverityBadge = (severity: string) => {
    const styles = {
      critical: 'badge bg-red-600 text-white',
      high: 'badge bg-orange-500 text-white',
      medium: 'badge bg-yellow-500 text-white',
      low: 'badge bg-blue-500 text-white',
    };
    return styles[severity as keyof typeof styles] || 'badge';
  };

  // Prepare table data from Sankey links
  const tableData = useMemo(() => {
    if (!sankeyData?.sankey?.links) return [];
    
    const links = sankeyData.sankey.links.map((link: SankeyLink) => {
      // Find source and target nodes for display names
      const sourceNode = sankeyData.sankey.nodes.find((n: SankeyNode) => n.id === link.source);
      const targetNode = sankeyData.sankey.nodes.find((n: SankeyNode) => n.id === link.target);
      
      return {
        source: link.source,
        sourceName: sourceNode?.name || link.source,
        target: link.target,
        targetName: targetNode?.name || link.target,
        sessions: link.sessions?.length || link.value || 0,
        value: link.value,
        link: link, // Store original link for drill-down
      };
    });
    
    // Sort data
    const sorted = [...links].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'source':
          comparison = a.sourceName.localeCompare(b.sourceName);
          break;
        case 'target':
          comparison = a.targetName.localeCompare(b.targetName);
          break;
        case 'sessions':
          comparison = a.sessions - b.sessions;
          break;
        default:
          comparison = 0;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });
    
    return sorted;
  }, [sankeyData, sortBy, sortOrder]);

  // Pagination for table
  const totalPages = Math.ceil(tableData.length / itemsPerPage);
  const paginatedData = tableData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handle table sort
  const handleTableSort = (column: typeof sortBy) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  // Sortable header component
  const SortableHeader = ({ 
    column, 
    children 
  }: { 
    column: typeof sortBy; 
    children: React.ReactNode 
  }) => (
    <th 
      className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase cursor-pointer hover:bg-secondary-100 dark:hover:bg-secondary-700 transition-colors"
      onClick={() => handleTableSort(column)}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortBy === column && (
          <ArrowUpDown className={`w-3 h-3 transition-transform ${sortOrder === 'desc' ? 'rotate-180' : ''}`} />
        )}
      </div>
    </th>
  );

  // Export filtered data as JSON
  const handleExport = () => {
    if (!sankeyData) {
      toast.error('No data to export');
      return;
    }

    try {
      // Prepare export data with ALL filtered data (not just current page)
      const exportData = {
        metadata: {
          exportDate: new Date().toISOString(),
          company: selectedCompany?.name,
          dataSource: selectedDataSource?.name,
          dateRange: {
            startDate: dateRange.startDate,
            endDate: dateRange.endDate,
            label: dateRange.label,
          },
          filters: {
            firstPage: firstPageFilter.value ? {
              operator: firstPageFilter.operator,
              value: firstPageFilter.value,
            } : null,
            lastPage: lastPageFilter.value ? {
              operator: lastPageFilter.operator,
              value: lastPageFilter.value,
            } : null,
            utmSource: utmSourceFilter !== 'all' ? utmSourceFilter : null,
            utmMedium: utmMediumFilter !== 'all' ? utmMediumFilter : null,
            utmCampaign: utmCampaignFilter !== 'all' ? utmCampaignFilter : null,
            utmTerm: utmTermFilter !== 'all' ? utmTermFilter : null,
            utmContent: utmContentFilter !== 'all' ? utmContentFilter : null,
            deviceType: deviceTypeFilter !== 'all' ? deviceTypeFilter : null,
          },
          totalSessions: sankeyData.totalSessions,
          totalNodes: sankeyData.sankey?.nodes?.length || 0,
          totalLinks: sankeyData.sankey?.links?.length || 0,
        },
        nodes: sankeyData.sankey?.nodes || [],
        links: sankeyData.sankey?.links || [],
        loopPatterns: loopPatterns || [],
        anomalies: anomaliesData?.anomalies || [],
      };

      // Create blob and download
      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename with date and filters
      const dateStr = new Date().toISOString().split('T')[0];
      const filterStr = [
        utmSourceFilter !== 'all' ? `source-${utmSourceFilter}` : '',
        utmCampaignFilter !== 'all' ? `campaign-${utmCampaignFilter}` : '',
        firstPageFilter.value ? 'filtered' : '',
      ].filter(Boolean).join('_');
      
      const filename = `funnel-analysis_${dateStr}${filterStr ? '_' + filterStr : ''}.json`;
      link.download = filename;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.success(`Exported ${exportData.nodes.length} nodes and ${exportData.links.length} links`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    }
  };


  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-secondary-900 dark:text-secondary-50">
            Funnel Analysis
          </h1>
          <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
            {selectedCompany?.name} • {selectedDataSource?.name}
            {connected && (
              <span className="ml-2 inline-flex items-center gap-1 text-green-600">
                <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                Live
              </span>
            )}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
          <DateRangeFilter value={dateRange} onChange={setDateRange} />
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="btn btn-secondary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <Filter className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="hidden xs:inline">Filters</span>
            <span className="xs:hidden">Filter</span>
          </button>
          <button
            onClick={() => refetchSankey()}
            className="btn btn-primary flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm px-2 sm:px-4 py-2"
          >
            <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4 shrink-0" />
            <span className="hidden xs:inline">Refresh</span>
          </button>
        </div>
      </div>
      {/* Real-time Alerts */}
      {alertsData?.alerts && alertsData.alerts.length > 0 && (
        <div className="card bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-red-600 mt-1" size={24} />
            <div className="flex-1">
              <h3 className="font-semibold text-red-900 dark:text-red-100">
                ⚠️ Real-time Alerts ({alertsData.alerts.length})
              </h3>
              <div className="mt-2 space-y-2">
                {alertsData.alerts.slice(0, 3).map((alert: any, index: number) => (
                  <div
                    key={index}
                    className="text-sm text-red-800 dark:text-red-200 flex items-center gap-2"
                  >
                    <span className={getSeverityBadge(alert.severity)}>{alert.severity}</span>
                    <span>
                      {alert.page} - {alert.exitRate.toFixed(1)}% exit rate (
                      {alert.affectedSessions} sessions)
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Filters Panel */}
      {showFilters && (
        <div className="card">
          <h3 className="font-semold mb-4">Filters</h3>

          {/* Page Filters - 2 columns responsive - WITH OPERATORS */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
            {/* First Page - Operator-based Filter */}
            <div className="relative">
              <label className="block text-sm font-medium mb-2">
                First Page Filter
                <span className="ml-2 text-xs text-secondary-500">(Groups similar URLs)</span>
              </label>
              
              <div className="flex gap-2">
                {/* Operator Dropdown */}
                <select
                  value={firstPageFilter.operator}
                  onChange={(e) => setFirstPageFilter({
                    ...firstPageFilter,
                    operator: e.target.value as any
                  })}
                  className="input w-40 flex-shrink-0"
                >
                  <option value="contains">Contains</option>
                  <option value="equals">Is Exactly</option>
                  <option value="startsWith">Starts With</option>
                  <option value="endsWith">Ends With</option>
                  <option value="notContains">Does Not Contain</option>
                </select>
                
                {/* Value Input with Autocomplete */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Type or select from list..."
                    value={firstPageFilter.value}
                    onChange={(e) => {
                      setFirstPageFilter({
                        ...firstPageFilter,
                        value: e.target.value
                      });
                      setFirstPageSearch(e.target.value);
                      setShowFirstPageDropdown(true);
                    }}
                    onFocus={() => setShowFirstPageDropdown(true)}
                    onBlur={() => setTimeout(() => setShowFirstPageDropdown(false), 200)}
                    className="input w-full"
                  />
                  
                  {/* Autocomplete Dropdown */}
                  {showFirstPageDropdown && availablePages.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg shadow-lg">
                      {availablePages
                        .filter((page: any) => {
                          // If no filter value, show all
                          if (!firstPageFilter.value) return true;
                          
                          // Search by: path (URL), full display name, and title part (before |)
                          const searchValue = firstPageFilter.value.toLowerCase();
                          const pagePath = (page.page || '').toLowerCase();
                          const displayName = (page.name || '').toLowerCase();
                          
                          // Extract title part (before the | or [ character)
                          const titlePart = displayName.split('|')[0].split('[')[0].trim();
                          
                          // Match if search value is in: path, full name, or just the title part
                          return pagePath.includes(searchValue) ||
                                 displayName.includes(searchValue) ||
                                 titlePart.includes(searchValue);
                        })
                        .slice(0, 20)
                        .map((page: any) => (
                          <button
                            key={page.page}
                            type="button"
                            onClick={() => {
                              setFirstPageFilter({
                                operator: 'equals',
                                value: page.page
                              });
                              setShowFirstPageDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-secondary-100 dark:hover:bg-secondary-700 border-b border-secondary-100 dark:border-secondary-700 last:border-0"
                          >
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-sm text-secondary-900 dark:text-secondary-100 truncate">
                                  {page.name}
                                </span>
                                <span className="text-xs text-secondary-500 shrink-0">
                                  {page.count.toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs text-secondary-500 dark:text-secondary-400 break-all">
                                {page.page}
                              </span>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                
                {/* Clear Button */}
                {firstPageFilter.value && (
                  <button
                    type="button"
                    onClick={() => setFirstPageFilter({ ...firstPageFilter, value: '' })}
                    className="px-3 py-2 text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300"
                    title="Clear filter"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Example hint */}
              <p className="text-xs text-secondary-500 mt-1">
                💡 Search by page title (e.g., "Checkout") or path (e.g., "/payment-confirmed")
              </p>
            </div>

            {/* Last Page - Operator-based Filter */}
            <div className="relative">
              <label className="block text-sm font-medium mb-2">
                Last Page Filter
                <span className="ml-2 text-xs text-secondary-500">(Groups similar URLs)</span>
              </label>
              
              <div className="flex gap-2">
                {/* Operator Dropdown */}
                <select
                  value={lastPageFilter.operator}
                  onChange={(e) => setLastPageFilter({
                    ...lastPageFilter,
                    operator: e.target.value as any
                  })}
                  className="input w-40 flex-shrink-0"
                >
                  <option value="contains">Contains</option>
                  <option value="equals">Is Exactly</option>
                  <option value="startsWith">Starts With</option>
                  <option value="endsWith">Ends With</option>
                  <option value="notContains">Does Not Contain</option>
                </select>
                
                {/* Value Input with Autocomplete */}
                <div className="relative flex-1">
                  <input
                    type="text"
                    placeholder="Type or select from list..."
                    value={lastPageFilter.value}
                    onChange={(e) => {
                      setLastPageFilter({
                        ...lastPageFilter,
                        value: e.target.value
                      });
                      setLastPageSearch(e.target.value);
                      setShowLastPageDropdown(true);
                    }}
                    onFocus={() => setShowLastPageDropdown(true)}
                    onBlur={() => setTimeout(() => setShowLastPageDropdown(false), 200)}
                    className="input w-full"
                  />
                  
                  {/* Autocomplete Dropdown */}
                  {showLastPageDropdown && availablePages.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto bg-white dark:bg-secondary-800 border border-secondary-300 dark:border-secondary-600 rounded-lg shadow-lg">
                      {availablePages
                        .filter((page: any) => {
                          // If no filter value, show all
                          if (!lastPageFilter.value) return true;
                          
                          // Search by: path (URL), full display name, and title part (before |)
                          const searchValue = lastPageFilter.value.toLowerCase();
                          const pagePath = (page.page || '').toLowerCase();
                          const displayName = (page.name || '').toLowerCase();
                          
                          // Extract title part (before the | or [ character)
                          const titlePart = displayName.split('|')[0].split('[')[0].trim();
                          
                          // Match if search value is in: path, full name, or just the title part
                          return pagePath.includes(searchValue) ||
                                 displayName.includes(searchValue) ||
                                 titlePart.includes(searchValue);
                        })
                        .slice(0, 20)
                        .map((page: any) => (
                          <button
                            key={page.page}
                            type="button"
                            onClick={() => {
                              setLastPageFilter({
                                operator: 'equals',
                                value: page.page
                              });
                              setShowLastPageDropdown(false);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-secondary-100 dark:hover:bg-secondary-700 border-b border-secondary-100 dark:border-secondary-700 last:border-0"
                          >
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center justify-between gap-2">
                                <span className="font-medium text-sm text-secondary-900 dark:text-secondary-100 truncate">
                                  {page.name}
                                </span>
                                <span className="text-xs text-secondary-500 shrink-0">
                                  {page.count.toLocaleString()}
                                </span>
                              </div>
                              <span className="text-xs text-secondary-500 dark:text-secondary-400 break-all">
                                {page.page}
                              </span>
                            </div>
                          </button>
                        ))}
                    </div>
                  )}
                </div>
                
                {/* Clear Button */}
                {lastPageFilter.value && (
                  <button
                    type="button"
                    onClick={() => setLastPageFilter({ ...lastPageFilter, value: '' })}
                    className="px-3 py-2 text-secondary-500 hover:text-secondary-700 dark:hover:text-secondary-300"
                    title="Clear filter"
                  >
                    ×
                  </button>
                )}
              </div>
              
              {/* Example hint */}
              <p className="text-xs text-secondary-500 mt-1">
                💡 Search by page title (e.g., "Checkout") or path (e.g., "/checkout")
              </p>
            </div>
          </div>

          {/* NEW TRAFFIC & DEVICE FILTERS - Cascading UTM Parameters */}
          <div className="border-t border-secondary-200 dark:border-secondary-700 pt-4">
            <h4 className="font-medium mb-3 text-sm">UTM Parameters (Cascading Filters)</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
              {/* UTM Source */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  UTM Source
                  {isLoadingUtmSources && (
                    <span className="ml-2 text-xs text-secondary-500 animate-pulse">
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  value={utmSourceFilter}
                  onChange={e => {
                    setUtmSourceFilter(e.target.value);
                    // Reset dependent filters
                    setUtmMediumFilter('all');
                    setUtmCampaignFilter('all');
                    setUtmTermFilter('all');
                    setUtmContentFilter('all');
                  }}
                  className="input"
                  disabled={isLoadingUtmSources}
                >
                  <option value="all">{isLoadingUtmSources ? 'Loading...' : 'All Sources'}</option>
                  {utmSources?.map((source: any) => (
                    <option key={source.value} value={source.value}>
                      {source.label} ({source.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* UTM Medium */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  UTM Medium
                  {isLoadingUtmMediums && (
                    <span className="ml-2 text-xs text-secondary-500 animate-pulse">
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  value={utmMediumFilter}
                  onChange={e => {
                    setUtmMediumFilter(e.target.value);
                    // Reset dependent filters
                    setUtmCampaignFilter('all');
                    setUtmTermFilter('all');
                    setUtmContentFilter('all');
                  }}
                  className="input"
                  disabled={isLoadingUtmMediums}
                >
                  <option value="all">{isLoadingUtmMediums ? 'Loading...' : 'All Mediums'}</option>
                  {utmMediums?.map((medium: any) => (
                    <option key={medium.value} value={medium.value}>
                      {medium.label} ({medium.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* UTM Campaign */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  UTM Campaign
                  {isLoadingUtmCampaigns && (
                    <span className="ml-2 text-xs text-secondary-500 animate-pulse">
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  value={utmCampaignFilter}
                  onChange={e => {
                    setUtmCampaignFilter(e.target.value);
                    // Reset dependent filters
                    setUtmTermFilter('all');
                    setUtmContentFilter('all');
                  }}
                  className="input"
                  disabled={isLoadingUtmCampaigns}
                >
                  <option value="all">
                    {isLoadingUtmCampaigns ? 'Loading...' : 'All Campaigns'}
                  </option>
                  {utmCampaigns?.map((campaign: any) => (
                    <option key={campaign.value} value={campaign.value}>
                      {campaign.label} ({campaign.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* UTM Term */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  UTM Term (Keyword/Ad ID)
                  {isLoadingUtmTerms && (
                    <span className="ml-2 text-xs text-secondary-500 animate-pulse">
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  value={utmTermFilter}
                  onChange={e => {
                    setUtmTermFilter(e.target.value);
                    // Reset dependent filters
                    setUtmContentFilter('all');
                  }}
                  className="input"
                  disabled={isLoadingUtmTerms}
                >
                  <option value="all">{isLoadingUtmTerms ? 'Loading...' : 'All Terms'}</option>
                  {utmTerms?.map((term: any) => (
                    <option key={term.value} value={term.value}>
                      {term.label} ({term.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* UTM Content */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  UTM Content (Ad Variant)
                  {isLoadingUtmContents && (
                    <span className="ml-2 text-xs text-secondary-500 animate-pulse">
                      Loading...
                    </span>
                  )}
                </label>
                <select
                  value={utmContentFilter}
                  onChange={e => setUtmContentFilter(e.target.value)}
                  className="input"
                  disabled={isLoadingUtmContents}
                >
                  <option value="all">{isLoadingUtmContents ? 'Loading...' : 'All Content'}</option>
                  {utmContents?.map((content: any) => (
                    <option key={content.value} value={content.value}>
                      {content.label} ({content.count.toLocaleString()})
                    </option>
                  ))}
                </select>
              </div>

              {/* Device Type */}
              <div>
                <label className="block text-sm font-medium mb-2">Device Type</label>
                <select
                  value={deviceTypeFilter}
                  onChange={e => setDeviceTypeFilter(e.target.value)}
                  className="input"
                >
                  <option value="all">All Devices</option>
                  <option value="mobile">Mobile</option>
                  <option value="desktop">Desktop</option>
                  <option value="tablet">Tablet</option>
                </select>
              </div>
            </div>

            {/* Active Filters Summary */}
            {(utmSourceFilter !== 'all' ||
              utmMediumFilter !== 'all' ||
              utmCampaignFilter !== 'all' ||
              utmTermFilter !== 'all' ||
              utmContentFilter !== 'all' ||
              deviceTypeFilter !== 'all' ||
              firstPageFilter.value ||
              lastPageFilter.value) && (
              <div className="mt-4 p-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800">
                <div className="flex items-center gap-2 flex-wrap text-sm">
                  <span className="font-medium">Active Filters:</span>
                  {firstPageFilter.value && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      First Page {firstPageFilter.operator}: "{firstPageFilter.value}"
                    </span>
                  )}
                  {lastPageFilter.value && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Last Page {lastPageFilter.operator}: "{lastPageFilter.value}"
                    </span>
                  )}
                  {utmSourceFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Source: {utmSourceFilter}
                    </span>
                  )}
                  {utmMediumFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Medium: {utmMediumFilter}
                    </span>
                  )}
                  {utmCampaignFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Campaign: {utmCampaignFilter}
                    </span>
                  )}
                  {utmTermFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Term: {utmTermFilter}
                    </span>
                  )}
                  {utmContentFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Content: {utmContentFilter}
                    </span>
                  )}
                  {deviceTypeFilter !== 'all' && (
                    <span className="px-2 py-1 bg-primary-100 dark:bg-primary-800 rounded-md">
                      Device: {deviceTypeFilter}
                    </span>
                  )}
                  <button
                    onClick={handleClearAllFilters}
                    className="flex items-center gap-1.5 text-xs text-primary-600 hover:text-primary-700 font-medium ml-auto"
                  >
                    <XCircle className="w-3.5 h-3.5" />
                    Clear All Filters
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Stats Cards - Row 1: Core Traffic Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Total Sessions</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.total_sessions?.toLocaleString() || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Monitor className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Desktop</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.desktop_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Smartphone className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Mobile</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.mobile_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <FileText className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Avg Pages/Session
              </p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.avg_pages_per_session?.toFixed(1) || '0.0'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
              <Clock className="w-6 h-6 text-cyan-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">
                Avg Duration
              </p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.avg_duration_seconds 
                    ? `${Math.floor(sankeyData.stats.avg_duration_seconds / 60)}m ${Math.floor(sankeyData.stats.avg_duration_seconds % 60)}s`
                    : '0m 0s'}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Row 2: Engagement & Quality Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mt-6">
        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <LogOut className="w-6 h-6 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Bounce Rate</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.bounce_rate || '0'}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">High Engagement</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.high_engagement_rate || '0'}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
              <Activity className="w-6 h-6 text-rose-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Active Now</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.active_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-yellow-100 dark:bg-yellow-900/30 flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Paid Traffic</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.paid_traffic_rate || '0'}%
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="card card-hover">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <Flame className="w-6 h-6 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Power Users</p>
              {isLoadingSankey ? (
                <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
              ) : (
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                  {sankeyData?.stats?.long_sessions || 0}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Row 3: Drop-Off Analysis */}
      {sankeyData?.stats?.drop_off_analysis && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-6">
          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Drop-Off Points</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                    {sankeyData?.stats?.drop_off_analysis?.total_drop_off_points || 0}
                  </p>
                )}
                <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                  Unique exit pages
                </p>
              </div>
            </div>
          </div>

          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Critical Zones</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                    {sankeyData?.stats?.drop_off_analysis?.critical_drop_off_zones || 0}
                  </p>
                )}
                <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                  &gt;50% exit rate
                </p>
              </div>
            </div>
          </div>

          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Avg Exit Rate</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                    {sankeyData?.stats?.drop_off_analysis?.avg_drop_off_rate || 0}%
                  </p>
                )}
                <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                  All pages average
                </p>
              </div>
            </div>
          </div>

          <div className="card card-hover cursor-pointer hover:shadow-lg transition-shadow" title={sankeyData?.stats?.drop_off_analysis?.highest_drop_off_page?.title || 'N/A'}>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                <LogOut className="w-6 h-6 text-rose-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Highest Drop-Off</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : sankeyData?.stats?.drop_off_analysis?.highest_drop_off_page ? (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData.stats.drop_off_analysis.highest_drop_off_page.exit_rate}%
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1 truncate">
                      {sankeyData.stats.drop_off_analysis.highest_drop_off_page.title || sankeyData.stats.drop_off_analysis.highest_drop_off_page.url}
                    </p>
                  </>
                ) : (
                  <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">N/A</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards - Row 4: E-commerce Conversion Funnel */}
      {sankeyData?.stats?.conversion_funnel && (
        <div className="grid grid-cols-1 md:grid-cols-6 gap-6 mt-6">
          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.consultation_viewed || 0,
                rate: sankeyData?.stats?.conversion_funnel?.consultation_view_rate || 0,
                total: sankeyData?.stats?.total_sessions || 0,
                abandoned: sankeyData?.stats?.conversion_funnel?.consultation_abandoned || 0,
                abandonmentRate: sankeyData?.stats?.conversion_funnel?.consultation_abandonment_rate || 0,
                proceeded: sankeyData?.stats?.conversion_funnel?.consultation_proceeded || 0,
                description: 'Users who viewed consultation or treatment pages',
              };
              setSelectedStat({
                title: 'Consultation Viewed',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('consultation_viewed', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-cyan-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Consultation Viewed</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData?.stats?.conversion_funnel?.consultation_viewed || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      {sankeyData?.stats?.conversion_funnel?.consultation_abandonment_rate || 0}% abandoned
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const dropOff = (sankeyData?.stats?.conversion_funnel?.checkout_started || 0) - (sankeyData?.stats?.conversion_funnel?.payment_attempted || 0);
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.checkout_started || 0,
                rate: sankeyData?.stats?.conversion_funnel?.checkout_rate || 0,
                total: sankeyData?.stats?.total_sessions || 0,
                description: 'Users who added items to cart and initiated checkout process',
                nextStep: 'Payment Attempted',
                nextValue: sankeyData?.stats?.conversion_funnel?.payment_attempted || 0,
                dropOff: dropOff,
                dropOffRate: sankeyData?.stats?.conversion_funnel?.checkout_started > 0 
                  ? ((dropOff / sankeyData?.stats?.conversion_funnel?.checkout_started) * 100).toFixed(1)
                  : '0.0',
              };
              setSelectedStat({
                title: 'Checkout Started',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('checkout_started', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <FileText className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Checkout Started</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData?.stats?.conversion_funnel?.checkout_started || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      {sankeyData?.stats?.conversion_funnel?.checkout_rate || 0}% of sessions
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.payment_attempted || 0,
                successRate: sankeyData?.stats?.conversion_funnel?.payment_success_rate || 0,
                successful: sankeyData?.stats?.conversion_funnel?.order_placed || 0,
                failed: sankeyData?.stats?.conversion_funnel?.payment_failed || 0,
                description: 'Users who reached the payment page',
                checkoutStarted: sankeyData?.stats?.conversion_funnel?.checkout_started || 0,
              };
              setSelectedStat({
                title: 'Payment Attempted',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('payment_attempted', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Payment Attempted</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData?.stats?.conversion_funnel?.payment_attempted || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      {sankeyData?.stats?.conversion_funnel?.payment_success_rate || 0}% success
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.order_placed || 0,
                conversionRate: sankeyData?.stats?.conversion_funnel?.conversion_rate || 0,
                total: sankeyData?.stats?.total_sessions || 0,
                checkoutStarted: sankeyData?.stats?.conversion_funnel?.checkout_started || 0,
                description: 'Successfully completed orders',
              };
              setSelectedStat({
                title: 'Orders Placed',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('order_placed', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Orders Placed</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-green-600">
                      {sankeyData?.stats?.conversion_funnel?.order_placed || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      {sankeyData?.stats?.conversion_funnel?.conversion_rate || 0}% conversion
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.abandoned_at_checkout || 0,
                rate: sankeyData?.stats?.conversion_funnel?.checkout_abandonment_rate || 0,
                checkoutStarted: sankeyData?.stats?.conversion_funnel?.checkout_started || 0,
                ordersPlaced: sankeyData?.stats?.conversion_funnel?.order_placed || 0,
                abandoned: sankeyData?.stats?.conversion_funnel?.abandoned_at_checkout || 0,
                abandonmentRate: sankeyData?.stats?.conversion_funnel?.checkout_abandonment_rate || 0,
                description: 'Users who started checkout but did not complete their order',
              };
              setSelectedStat({
                title: 'Cart Abandonment',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('abandoned_at_checkout', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                <LogOut className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Cart Abandonment</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData?.stats?.conversion_funnel?.abandoned_at_checkout || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      {sankeyData?.stats?.conversion_funnel?.checkout_abandonment_rate || 0}% abandoned
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div 
            className="card card-hover cursor-pointer transition-all hover:shadow-xl hover:scale-105"
            onClick={async () => {
              const failureRate = sankeyData?.stats?.conversion_funnel?.payment_attempted > 0 
                ? (((sankeyData?.stats?.conversion_funnel?.payment_failed || 0) / sankeyData?.stats?.conversion_funnel?.payment_attempted) * 100).toFixed(1)
                : '0.0';
              const statData = {
                value: sankeyData?.stats?.conversion_funnel?.payment_failed || 0,
                paymentAttempted: sankeyData?.stats?.conversion_funnel?.payment_attempted || 0,
                failureRate: failureRate,
                description: 'Payment attempts that failed or were declined',
              };
              setSelectedStat({
                title: 'Payment Failed',
                type: 'conversion',
                data: statData,
              });
              // Fetch AI insights for this stat
              await fetchAIInsights('payment_failed', statData);
            }}
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Payment Failed</p>
                {isLoadingSankey ? (
                  <div className="h-8 w-20 bg-secondary-200 dark:bg-secondary-700 animate-pulse rounded mt-1" />
                ) : (
                  <>
                    <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                      {sankeyData?.stats?.conversion_funnel?.payment_failed || 0}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-500 mt-1">
                      Payment issues
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Anomalies Row - Keep existing anomaly tiles in a separate row if needed */}
      {(anomaliesData?.criticalCount > 0 || anomaliesData?.highCount > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">Critical Anomalies</p>
                <p className="text-2xl font-bold text-red-600 mt-1">
                {anomaliesData?.criticalCount || 0}
              </p>
            </div>
          </div>
        </div>

          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
                <TrendingDown className="w-6 h-6 text-orange-600" />
              </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">High Severity</p>
                <p className="text-2xl font-bold text-orange-600 mt-1">
                {anomaliesData?.highCount || 0}
              </p>
            </div>
          </div>
        </div>

          <div className="card card-hover">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
            <div>
              <p className="text-sm text-secondary-600 dark:text-secondary-400">Total Anomalies</p>
                <p className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 mt-1">
                  {anomaliesData?.totalAnomalies || 0}
                </p>
            </div>
          </div>
        </div>
      </div>
      )}
      {/* Main User Journey Flow - Full Width Scrollable */}
      <div className="w-full" style={{ maxWidth: 'none' }}>
        <div className="card" style={{ overflow: 'visible', maxWidth: 'none' }}>
          <div className="flex flex-col gap-4 p-6" style={{ maxWidth: 'none' }}>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50 flex items-center gap-2">
                  User Journey Flow
                  <div className="group relative">
                    <Info
                      size={18}
                      className="text-secondary-400 hover:text-primary-600 cursor-help transition-colors"
                    />
                    <div className="absolute left-0 top-8 w-80 bg-white dark:bg-secondary-800 border border-secondary-200 dark:border-secondary-700 rounded-lg shadow-lg p-4 text-sm font-normal opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <p className="font-semibold mb-2 text-primary-600 dark:text-primary-400">
                        📊 How to Read This:
                      </p>
                      <div className="space-y-2 text-secondary-700 dark:text-secondary-300">
                        <p>
                          <strong>Sankey Graph:</strong> Visual flow showing journey paths with thickness representing traffic strength
                        </p>
                        <p>
                          <strong>Table View:</strong> Detailed list of all page transitions with session counts
                        </p>
                        <p>
                          <strong>Click any row/link:</strong> Drill down to see actual sessions
                        </p>
                      </div>
                    </div>
                  </div>
                </h2>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-1">
                  {viewMode === 'graph' 
                    ? 'Visual representation of user paths • Min. 2 sessions per path'
                    : `Showing ${tableData.length} page transitions • ${paginatedData.length} per page`
                  }
                </p>
              </div>
              <div className="flex items-center gap-2">
                {/* View Mode Toggle */}
                <div className="flex items-center gap-1 bg-secondary-100 dark:bg-secondary-800 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('graph')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all text-sm font-medium ${
                      viewMode === 'graph'
                        ? 'bg-white dark:bg-secondary-700 text-primary-600 shadow-sm'
                        : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                    }`}
                  >
                    <BarChart3 size={16} />
                    <span className="hidden sm:inline">Sankey</span>
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md transition-all text-sm font-medium ${
                      viewMode === 'table'
                        ? 'bg-white dark:bg-secondary-700 text-primary-600 shadow-sm'
                        : 'text-secondary-600 dark:text-secondary-400 hover:text-secondary-900 dark:hover:text-secondary-200'
                    }`}
                  >
                    <TableIcon size={16} />
                    <span className="hidden sm:inline">Table</span>
                  </button>
                </div>
                <button className="btn btn-outline flex items-center gap-2" onClick={handleExport}>
                  <Download size={16} />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
          </div>

          {/* Content - Graph or Table */}
          {isLoadingSankey ? (
            <div className="h-[calc(100vh-300px)] flex items-center justify-center">
              <div className="text-center">
                <div className="spinner mx-auto mb-4"></div>
                <p className="text-sm text-secondary-600 dark:text-secondary-400">
                  Loading user journey data...
                </p>
              </div>
            </div>
          ) : sankeyData?.sankey &&
            sankeyData.sankey.nodes?.length > 0 &&
            sankeyData.sankey.links?.length > 0 ? (
            <>
              {viewMode === 'graph' ? (
                <>
                  {/* Scroll Hint */}
                  <div className="mb-2 px-4 py-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg text-sm text-blue-700 dark:text-blue-300 flex items-center gap-2 mx-6">
                    <ChevronRight className="w-4 h-4" />
                    <span>
                      <strong>Tip:</strong> Scroll horizontally to see all journey stages. Click any flow to drill down.
                    </span>
                  </div>

                  {/* Sankey in full-width scrollable container */}
                  <div className="w-full overflow-x-auto" style={{ maxWidth: 'none' }}>
                    <SankeyDiagram
                      nodes={sankeyData.sankey.nodes}
                      links={sankeyData.sankey.links}
                      onLinkClick={handleLinkClick}
                      onNodeClick={handleNodeClick}
                      onLoopsDetected={setLoopPatterns}
                      height={Math.max(1200, window.innerHeight - 250)}
                      maxNodes={50}
                      minSessions={2}
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Table View */}
                  <div className="overflow-x-auto px-6 pb-6">
                    <table className="table w-full">
                      <thead>
                        <tr>
                          <SortableHeader column="source">From Page</SortableHeader>
                          <SortableHeader column="target">To Page</SortableHeader>
                          <SortableHeader column="sessions">Sessions</SortableHeader>
                          <th className="px-4 py-3 text-left text-xs font-medium text-secondary-500 uppercase">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedData.map((row, index) => (
                          <tr 
                            key={`${row.source}-${row.target}-${index}`}
                            className="hover:bg-secondary-50 dark:hover:bg-secondary-800/50 transition-colors"
                          >
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                <span className="font-medium text-sm text-secondary-900 dark:text-secondary-100">
                                  {row.sourceName}
                                </span>
                                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                  {row.source}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-col gap-1">
                                <span className="font-medium text-sm text-secondary-900 dark:text-secondary-100">
                                  {row.targetName}
                                </span>
                                <span className="text-xs text-secondary-500 dark:text-secondary-400">
                                  {row.target}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200">
                                {row.sessions.toLocaleString()}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <button
                                onClick={() => handleLinkClick(row.link)}
                                className="btn btn-sm btn-outline text-xs"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="flex items-center justify-between mt-6 border-t border-secondary-200 dark:border-secondary-700 pt-4">
                        <div className="text-sm text-secondary-600 dark:text-secondary-400">
                          Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, tableData.length)} of {tableData.length} transitions
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                            disabled={currentPage === 1}
                            className="btn btn-sm btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <ChevronLeft size={16} />
                            Previous
                          </button>
                          <div className="flex items-center gap-1">
                            {[...Array(totalPages)].map((_, i) => {
                              const page = i + 1;
                              // Show first, last, current, and pages around current
                              if (
                                page === 1 ||
                                page === totalPages ||
                                (page >= currentPage - 1 && page <= currentPage + 1)
                              ) {
                                return (
                                  <button
                                    key={page}
                                    onClick={() => setCurrentPage(page)}
                                    className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                                      currentPage === page
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-secondary-100 dark:bg-secondary-800 text-secondary-700 dark:text-secondary-300 hover:bg-secondary-200 dark:hover:bg-secondary-700'
                                    }`}
                                  >
                                    {page}
                                  </button>
                                );
                              } else if (
                                page === currentPage - 2 ||
                                page === currentPage + 2
                              ) {
                                return <span key={page} className="px-2">...</span>;
                              }
                              return null;
                            })}
                          </div>
                          <button
                            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                            disabled={currentPage === totalPages}
                            className="btn btn-sm btn-outline disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            Next
                            <ChevronRightIcon size={16} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="h-[calc(100vh-300px)] flex items-center justify-center text-secondary-400">
              <div className="text-center">
                <AlertTriangle className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">
                  {sankeyData?.totalSessions > 0
                    ? 'No user journeys found with multiple pages'
                    : 'No data available'}
                </p>
                <p className="text-sm mt-2">
                  {sankeyData?.totalSessions > 0
                    ? `Found ${sankeyData.totalSessions} sessions, but they don't have page-to-page transitions. Try adjusting your date range or filters.`
                    : 'Try adjusting your date range or filters'}
                </p>
              </div>
            </div>
          )}
        </div>{' '}
        {/* Close card */}
        {/* Loop Analysis Section */}
        {loopPatterns && loopPatterns.length > 0 && sankeyData?.totalSessions && (
          <div className="mt-6">
            <LoopAnalysis 
              loops={loopPatterns} 
              totalSessions={sankeyData.totalSessions}
              companyId={companyId}
              dataSourceId={dataSourceId}
              dateRange={{
                startDate: dateRange.startDate,
                endDate: dateRange.endDate,
              }}
            />
          </div>
        )}
      </div>{' '}
      {/* Close w-full -mx-4 lg:-mx-6 container */}
      {/* Session Drill-Down Modal/Drawer */}
      <SessionDrillDownModal
        isOpen={isDrillDownOpen}
        onClose={closeDrillDown}
        sourcePage={selectedLink?.source || ''}
        targetPage={selectedLink?.target || ''}
        sessions={linkSessionsData?.sessions || []}
        totalSessions={linkSessionsData?.total || 0}
        isLoading={isLoadingLinkSessions}
        onSessionClick={handleSessionClick}
        selectedSessionData={sessionData}
      />
      {/* Anomalies List */}
      {anomaliesData?.anomalies && anomaliesData.anomalies.length > 0 && (
        <div className="card">
          <h2 className="text-xl font-semibold mb-4">Detected Anomalies</h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Page</th>
                  <th>Current Rate</th>
                  <th>Baseline</th>
                  <th>Deviation</th>
                  <th>Affected Sessions</th>
                </tr>
              </thead>
              <tbody>
                {anomaliesData.anomalies.map((anomaly: Anomaly, index: number) => (
                  <tr key={index}>
                    <td>
                      <span className={getSeverityBadge(anomaly.severity)}>{anomaly.severity}</span>
                    </td>
                    <td className="font-medium">{anomaly.page}</td>
                    <td>{anomaly.current.toFixed(1)}%</td>
                    <td>{anomaly.baseline.toFixed(1)}%</td>
                    <td>{anomaly.deviation.toFixed(2)}σ</td>
                    <td>{anomaly.affected_sessions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      
      {/* Stats Detail Modal */}
      {selectedStat && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={() => {
            setSelectedStat(null);
            setExpandedSections({});
          }}
        >
          <div 
            className="bg-white dark:bg-secondary-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-secondary-200 dark:border-secondary-700 flex items-center justify-between">
              <h3 className="text-2xl font-bold text-secondary-900 dark:text-secondary-50">
                {selectedStat.title}
              </h3>
              <button
                onClick={() => {
                  setSelectedStat(null);
                  setExpandedSections({});
                  setAbandonedSessions([]);
                }}
                className="p-2 hover:bg-secondary-100 dark:hover:bg-secondary-700 rounded-lg transition-colors"
              >
                <XCircle className="w-6 h-6 text-secondary-600 dark:text-secondary-400" />
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Description */}
              <p className="text-secondary-600 dark:text-secondary-400">
                {selectedStat.data.description}
              </p>
              
              {/* Main Value */}
              <div className="bg-primary-50 dark:bg-primary-900/20 rounded-lg p-6 text-center">
                <p className="text-5xl font-bold text-primary-600 dark:text-primary-400">
                  {selectedStat.data.value}
                </p>
                <p className="text-sm text-secondary-600 dark:text-secondary-400 mt-2">
                  {selectedStat.title}
                </p>
              </div>
              
              {/* Detailed Breakdown */}
              <div className="space-y-4">
                {selectedStat.data.rate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Rate</span>
                    <span className="font-bold text-secondary-900 dark:text-secondary-50">
                      {selectedStat.data.rate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.total !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Total Sessions</span>
                    <span className="font-bold text-secondary-900 dark:text-secondary-50">
                      {selectedStat.data.total}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.abandoned !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className="text-amber-700 dark:text-amber-300">Abandoned</span>
                    <span className="font-bold text-amber-900 dark:text-amber-50">
                      {selectedStat.data.abandoned}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.abandonmentRate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className="text-amber-700 dark:text-amber-300">Abandonment Rate</span>
                    <span className="font-bold text-amber-900 dark:text-amber-50">
                      {selectedStat.data.abandonmentRate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.proceeded !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <span className="text-green-700 dark:text-green-300">Proceeded</span>
                    <span className="font-bold text-green-900 dark:text-green-50">
                      {selectedStat.data.proceeded}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.successRate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <span className="text-green-700 dark:text-green-300">Success Rate</span>
                    <span className="font-bold text-green-900 dark:text-green-50">
                      {selectedStat.data.successRate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.successful !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Successful</span>
                    <span className="font-bold text-green-600 dark:text-green-400">
                      {selectedStat.data.successful}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.failed !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Failed</span>
                    <span className="font-bold text-red-600 dark:text-red-400">
                      {selectedStat.data.failed}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.conversionRate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <span className="text-green-700 dark:text-green-300">Conversion Rate</span>
                    <span className="font-bold text-green-900 dark:text-green-50">
                      {selectedStat.data.conversionRate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.dropOff !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Drop-off to Next Step</span>
                    <span className="font-bold text-secondary-900 dark:text-secondary-50">
                      {selectedStat.data.dropOff}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.dropOffRate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                    <span className="text-amber-700 dark:text-amber-300">Drop-off Rate</span>
                    <span className="font-bold text-amber-900 dark:text-amber-50">
                      {selectedStat.data.dropOffRate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.failureRate !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <span className="text-red-700 dark:text-red-300">Failure Rate</span>
                    <span className="font-bold text-red-900 dark:text-red-50">
                      {selectedStat.data.failureRate}%
                    </span>
                  </div>
                )}
                
                {selectedStat.data.checkoutStarted !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Checkout Started</span>
                    <span className="font-bold text-secondary-900 dark:text-secondary-50">
                      {selectedStat.data.checkoutStarted}
                    </span>
                  </div>
                )}
                
                {selectedStat.data.paymentAttempted !== undefined && (
                  <div className="flex justify-between items-center p-4 bg-secondary-50 dark:bg-secondary-700/50 rounded-lg">
                    <span className="text-secondary-700 dark:text-secondary-300">Payment Attempted</span>
                    <span className="font-bold text-secondary-900 dark:text-secondary-50">
                      {selectedStat.data.paymentAttempted}
                    </span>
                  </div>
                )}
              </div>
              
              {/* Expandable Section for Abandoned Sessions - ONLY show if abandoned data exists */}
              {selectedStat.data.abandoned !== undefined && selectedStat.data.abandoned > 0 && (
                <div className="border-t border-secondary-200 dark:border-secondary-700 pt-6 mt-6">
                  <button
                    onClick={async () => {
                      const wasExpanded = expandedSections.abandonedSessions;
                      setExpandedSections(prev => ({
                        ...prev,
                        abandonedSessions: !prev.abandonedSessions
                      }));
                      
                      // If expanding and no data loaded yet, fetch it
                      if (!wasExpanded && abandonedSessions.length === 0) {
                        setLoadingAbandonedSessions(true);
                        try {
                          const response = await api.get('/api/v1/analytics/sessions', {
                            params: {
                              companyId,
                              dataSourceId,
                              startDate: typeof dateRange.startDate === 'string' 
                                ? dateRange.startDate 
                                : dateRange.startDate?.toISOString?.() || new Date().toISOString(),
                              endDate: typeof dateRange.endDate === 'string' 
                                ? dateRange.endDate 
                                : dateRange.endDate?.toISOString?.() || new Date().toISOString(),
                              limit: 10, // Load first 10 abandoned sessions
                              page: 1,
                            },
                          });
                          
                          if (response.data?.success && response.data?.data?.sessions) {
                            setAbandonedSessions(response.data.data.sessions);
                          }
                        } catch (error) {
                          console.error('Failed to load abandoned sessions:', error);
                          toast.error('Failed to load abandoned sessions');
                        } finally {
                          setLoadingAbandonedSessions(false);
                        }
                      }
                    }}
                    className="w-full flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 hover:bg-amber-100 dark:hover:bg-amber-900/30 rounded-lg transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-600" />
                      <div className="text-left">
                        <p className="font-semibold text-amber-900 dark:text-amber-100">
                          View Abandoned Sessions
                        </p>
                        <p className="text-xs text-amber-700 dark:text-amber-300">
                          {selectedStat.data.abandoned} sessions that didn't complete this step
                        </p>
                      </div>
                    </div>
                    {expandedSections.abandonedSessions ? (
                      <ChevronUp className="w-5 h-5 text-amber-600" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-amber-600" />
                    )}
                  </button>
                  
                  {expandedSections.abandonedSessions && (
                    <div className="mt-4 p-4 bg-secondary-50 dark:bg-secondary-900/50 rounded-lg">
                      <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                          <Info className="w-4 h-4 inline mr-2" />
                          Showing up to 10 sample abandoned sessions. View all sessions on the Sessions page.
                        </p>
                      </div>
                      
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300">
                          What constitutes an abandoned session for "{selectedStat.title}":
                        </p>
                        
                        {selectedStat.data.description && (
                          <div className="p-3 bg-white dark:bg-secondary-800 rounded border border-secondary-200 dark:border-secondary-700">
                            <p className="text-sm text-secondary-600 dark:text-secondary-400">
                              {selectedStat.data.description}
                            </p>
                          </div>
                        )}
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="p-3 bg-white dark:bg-secondary-800 rounded border border-secondary-200 dark:border-secondary-700">
                            <p className="text-xs text-secondary-500 dark:text-secondary-500 mb-1">Total Abandoned</p>
                            <p className="text-2xl font-bold text-amber-600">{selectedStat.data.abandoned}</p>
                          </div>
                          
                          {selectedStat.data.abandonmentRate !== undefined && (
                            <div className="p-3 bg-white dark:bg-secondary-800 rounded border border-secondary-200 dark:border-secondary-700">
                              <p className="text-xs text-secondary-500 dark:text-secondary-500 mb-1">Abandonment Rate</p>
                              <p className="text-2xl font-bold text-amber-600">{selectedStat.data.abandonmentRate}%</p>
                            </div>
                          )}
                        </div>
                        
                        {/* Session List */}
                        {loadingAbandonedSessions ? (
                          <div className="flex items-center justify-center py-8">
                            <div className="spinner"></div>
                            <p className="ml-3 text-sm text-secondary-600 dark:text-secondary-400">
                              Loading sessions...
                            </p>
                          </div>
                        ) : abandonedSessions.length > 0 ? (
                          <div className="mt-4 space-y-2">
                            <p className="text-sm font-medium text-secondary-700 dark:text-secondary-300 mb-2">
                              Sample Abandoned Sessions:
                            </p>
                            {abandonedSessions.map((session) => (
                              <div
                                key={session.session_id}
                                className="p-3 bg-white dark:bg-secondary-800 rounded border border-secondary-200 dark:border-secondary-700 hover:border-amber-400 dark:hover:border-amber-600 transition-colors cursor-pointer"
                                onClick={() => {
                                  // Open session details
                                  console.log('Session clicked:', session);
                                }}
                              >
                                <div className="flex items-start justify-between gap-3">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-xs font-mono text-secondary-500 dark:text-secondary-500 truncate">
                                        {session.session_id}
                                      </span>
                                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        session.device === 'desktop'
                                          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300'
                                          : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'
                                      }`}>
                                        {session.device}
                                      </span>
                                    </div>
                                    <div className="text-sm text-secondary-700 dark:text-secondary-300 truncate">
                                      {session.last_page_title || session.last_page}
                                    </div>
                                    <div className="flex items-center gap-4 mt-2 text-xs text-secondary-500 dark:text-secondary-500">
                                      <span>{session.total_pages} pages</span>
                                      <span>{Math.round(session.duration / 1000)}s duration</span>
                                      {session.utm_source && (
                                        <span className="px-2 py-0.5 bg-secondary-100 dark:bg-secondary-700 rounded">
                                          {session.utm_source}
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <ChevronRight className="w-4 h-4 text-secondary-400 flex-shrink-0 mt-1" />
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : null}
                        
                        {/* Quick Action Button */}
                        <button
                          onClick={() => {
                            // Navigate to Sessions page with pre-applied filters
                            window.location.href = '/analytics/sessions';
                          }}
                          className="w-full mt-4 btn btn-primary btn-sm"
                        >
                          <ExternalLink className="w-4 h-4 mr-2" />
                          View All Abandoned Sessions in Detail
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* AI-Powered Insights & Recommendations */}
              <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg p-6 border-2 border-purple-200 dark:border-purple-700">
                <div className="flex items-center gap-2 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-lg font-bold text-purple-900 dark:text-purple-100">
                      AI-Powered Insights
                    </p>
                    <p className="text-xs text-purple-700 dark:text-purple-300">
                      Personalized recommendations from OpenAI
                    </p>
                  </div>
                </div>
                
                {loadingInsights ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="flex flex-col items-center gap-3">
                      <div className="spinner border-purple-600"></div>
                      <p className="text-sm text-purple-700 dark:text-purple-300 animate-pulse">
                        🤖 Analyzing your funnel data...
                      </p>
                    </div>
                  </div>
                ) : aiInsights ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <div className="text-sm text-purple-900 dark:text-purple-100 whitespace-pre-wrap leading-relaxed">
                      {aiInsights}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-purple-600 dark:text-purple-400">
                      AI insights are being generated...
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FunnelAnalysisPage;
